import { StateCreator } from 'zustand';
import { CedarStore } from './types';

// Define types that our state values can be
export type BasicStateValue =
	| string
	| number
	| boolean
	| object
	| unknown[]
	| undefined
	| void;

type ValueTypeString<T> = T extends string
	? 'string'
	: T extends number
		? 'number'
		: T extends boolean
			? 'boolean'
			: T extends unknown[]
				? 'array'
				: T extends undefined
					? 'undefined'
					: T extends void
						? 'void'
						: T extends object
							? 'object'
							: 'object'; // fallback

// Enum options type for state values that are enums
export type EnumOptions = Record<string, string> | string[];

// Setter types
export interface SetterParameter {
	name: string;
	type: string;
	description: string;
	optional?: boolean;
	enumOptions?: EnumOptions; // Add enum options for this specific parameter
}

// Define a flexible type for setter functions
export type SetterFunction<T = BasicStateValue> = (newValue: T) => void;

// Setter object that includes both metadata and execution function
export interface Setter<T = BasicStateValue> {
	name: string;
	description: string;
	parameters?: SetterParameter[];
	execute: SetterFunction<T>;
}

// Interface for a single user state object
export interface UserState<T = BasicStateValue> {
	key: string;
	value: T;
	valueType: ValueTypeString<T>;
	displayName?: string;
	enumOptions?: EnumOptions;
	setters: Record<string, Setter<T>>;
}

// Define the user state slice
export interface UserStateSlice {
	// State
	userStates: Record<string, UserState>;

	// Actions
	registerState: <T extends BasicStateValue>(config: {
		key: string;
		initialValue: T;
		displayName?: string;
		enumOptions?: EnumOptions;
		// If custom setters is not specified, and default setter is null, this state is view only.
		customSetters?: Record<string, Setter>;
		defaultSetter?: SetterFunction | null; // Accept a setter function directly. Set to null to have no default setter (can still use custom setters). If not set, Cedar will do state management and generate a default setter.
	}) => void;
	getState: <T extends BasicStateValue = BasicStateValue>(
		key: string
	) => UserState<T> | undefined;

	// Method to add custom setters to an existing state
	addCustomSetters: (key: string, setters: Record<string, Setter>) => boolean;

	// Format user state for copilot context
	formatStateForCopilot: (options?: {
		includeStateKeys?: string[];
		excludeStateKeys?: string[];
	}) => string;

	// Format available functions for copilot
	formatStateFunctionsForCopilot: () => string;
}

// Create the user state slice
export const createUserStateSlice: StateCreator<
	CedarStore,
	[],
	[],
	UserStateSlice
> = (set, get) => {
	return {
		// Default state
		userStates: {},

		// Register a new state or replace an existing one
		registerState: (config: {
			key: string;
			initialValue: BasicStateValue;
			displayName?: string;
			enumOptions?: EnumOptions;
			customSetters?: Record<string, Setter>;
			defaultSetter?: SetterFunction | null;
		}) => {
			set((state) => {
				const valueType = inferValueType(config.initialValue);

				function inferValueType<T>(value: T): ValueTypeString<T> {
					if (value === undefined || value === null)
						return 'void' as ValueTypeString<T>;
					if (Array.isArray(value)) return 'array' as ValueTypeString<T>;
					const type = typeof value;
					if (
						type === 'string' ||
						type === 'number' ||
						type === 'boolean' ||
						type === 'object' ||
						type === 'undefined'
					)
						return type as ValueTypeString<T>;
					return 'object' as ValueTypeString<T>;
				}
				// Create the default setter
				const createDefaultSetter = (): Setter => {
					return {
						name: 'setValue', // Fixed name for default setter
						description: `Set the entire value of ${config.displayName || config.key}`,
						parameters: [
							{
								name: 'newValue',
								type: valueType,
								description: `New value to set for ${config.displayName || config.key}`,
							},
						],
						execute:
							config.defaultSetter ||
							function (newValue: BasicStateValue) {
								// Type checking
								const newValueType = Array.isArray(newValue)
									? 'array'
									: typeof newValue;
								if (newValueType !== valueType) {
									console.error(
										`Type mismatch: Expected ${valueType}, got ${newValueType}`
									);
									return;
								}

								// Update state
								set((state) => ({
									userStates: {
										...state.userStates,
										[config.key]: {
											...state.userStates[config.key],
											value: newValue,
										},
									},
								}));
							},
					};
				};

				// Check if we have a placeholder state already
				const existingState = state.userStates[config.key];

				// Combine custom setters with default setter
				const finalSetters: Record<string, Setter> = {
					...(existingState?.setters || {}), // Keep existing setters if we have a placeholder
					...(config.customSetters || {}),
				};

				// Add default setter unless explicitly set to null
				if (config.defaultSetter !== null) {
					finalSetters['setValue'] = createDefaultSetter();
				}

				// Create the state object
				const userState: UserState = {
					key: config.key,
					value: config.initialValue,
					valueType,
					displayName: config.displayName,
					enumOptions: config.enumOptions,
					setters: finalSetters,
				};

				// Return updated state with the new/replaced user state
				return {
					userStates: {
						...state.userStates,
						[config.key]: userState,
					},
				};
			});
		},

		getState: <T extends BasicStateValue = BasicStateValue>(
			key: string
		): UserState<T> | undefined => {
			const state = get().userStates[key];
			return isUserState<T>(state)
				? { ...state, value: state.value as T }
				: undefined;
		},

		// Add custom setters to an existing state
		addCustomSetters: (
			key: string,
			setters: Record<string, Setter>
		): boolean => {
			const existingState = get().userStates[key];

			if (!existingState) {
				// Create a placeholder state with the setters
				// We use empty/default values that will be properly set when registerState is called
				console.info(
					`Creating placeholder state for "${key}" with custom setters`
				);
				set((state) => ({
					userStates: {
						...state.userStates,
						[key]: {
							key: key,
							value: '', // Default empty value
							valueType: 'string', // Default type
							setters: { ...setters },
							// Other fields will be properly set when registerState is called
						},
					},
				}));
				return true;
			}

			set((state) => ({
				userStates: {
					...state.userStates,
					[key]: {
						...state.userStates[key],
						// Merge existing setters with new ones
						setters: {
							...state.userStates[key].setters,
							...setters,
						},
					},
				},
			}));

			return true;
		},

		// Format user state for copilot context
		formatStateForCopilot: (options = {}) => {
			const { includeStateKeys = [], excludeStateKeys = [] } = options;

			const states = get().userStates;
			let result = '## User State Summary\n\n';

			// Filter states based on include/exclude options
			const stateKeys = Object.keys(states).filter((key) => {
				if (includeStateKeys.length > 0) {
					return includeStateKeys.includes(key);
				}
				if (excludeStateKeys.length > 0) {
					return !excludeStateKeys.includes(key);
				}
				return true;
			});

			// Format state information
			if (stateKeys.length > 0) {
				result += '### Current User States\n\n';

				stateKeys.forEach((key) => {
					const state = states[key];
					const displayName = state.displayName || key;

					result += `**${displayName}** (key: \`${key}\`)\n`;
					result += `- Type: \`${state.valueType}\`\n`;

					// Format value based on its type
					if (state.valueType === 'object') {
						result += `- Current value: \`${JSON.stringify(state.value, null, 2)}\`\n`;
					} else if (state.valueType === 'array') {
						result += `- Current value: \`${JSON.stringify(state.value)}\`\n`;
					} else {
						result += `- Current value: \`${state.value}\`\n`;
					}

					// Add enum options if available
					if (state.enumOptions) {
						result += '- Available options:\n';
						if (Array.isArray(state.enumOptions)) {
							state.enumOptions.forEach((value) => {
								result += `  - \`${value}\`\n`;
							});
						} else {
							Object.entries(state.enumOptions).forEach(([value, label]) => {
								result += `  - \`${value}\`: ${label}\n`;
							});
						}
					}

					// Add available setters in a way that focuses on state name, setter name, and parameter values
					if (Object.keys(state.setters).length > 0) {
						result += '- Available setters:\n';
						Object.values(state.setters).forEach((setter) => {
							result += `  - \`${setter.name}\`: ${setter.description}\n`;
							if (setter.parameters && setter.parameters.length > 0) {
								result += '    - Parameters:\n';
								setter.parameters.forEach((param) => {
									result += `      - \`${param.name}\` (${param.type})${param.optional ? ' (optional)' : ''}: ${param.description}\n`;
									if (param.enumOptions) {
										result += '        - Allowed values:\n';
										if (Array.isArray(param.enumOptions)) {
											param.enumOptions.forEach((value) => {
												result += `          - \`${value}\`\n`;
											});
										} else {
											Object.entries(param.enumOptions).forEach(
												([value, label]) => {
													result += `          - \`${value}\`: ${label}\n`;
												}
											);
										}
									}
								});
							}
						});
					}

					result += '\n';
				});
			} else {
				result += 'No user states are currently registered.\n\n';
			}

			return result;
		},

		// Format available functions for copilot (separate from state)
		formatStateFunctionsForCopilot: () => {
			const states = get().userStates;
			if (!states || Object.keys(states).length === 0) {
				return '';
			}

			let result = '## Available Functions\n\n';

			// Collect all setters from all states
			Object.entries(states).forEach(([stateKey, state]) => {
				if (state.setters && Object.keys(state.setters).length > 0) {
					// Add a section for this state's setters
					result += `### State: ${state.displayName || stateKey}\n\n`;
					result += `- State Key: \`${stateKey}\`\n`;

					// List each setter for this state
					Object.entries(state.setters).forEach(([setterName, setter]) => {
						result += `**${setter.name}** (for state \`${stateKey}\`)\n`;
						result += `- Description: ${setter.description}\n`;
						result += `- Full Reference: \`${stateKey}:${setterName}\`\n`;

						if (setter.parameters && setter.parameters.length > 0) {
							result += '- Parameters:\n';
							setter.parameters.forEach((param) => {
								result += `  - \`${param.name}\` (${param.type})${param.optional ? ' (optional)' : ''}: ${param.description}\n`;

								if (param.enumOptions) {
									result += '    - Allowed values:\n';
									if (Array.isArray(param.enumOptions)) {
										param.enumOptions.forEach((value) => {
											result += `      - \`${value}\`\n`;
										});
									} else {
										Object.entries(param.enumOptions).forEach(
											([value, label]) => {
												result += `      - \`${value}\`: ${label}\n`;
											}
										);
									}
								}
							});
						} else {
							result += '- Parameters: None\n';
						}

						result += '\n';
					});
				}
			});

			return result;
		},
	};
};

function isUserState<T>(value: unknown): value is UserState<T> {
	return (
		typeof value === 'object' &&
		value !== null &&
		'value' in value &&
		'key' in value &&
		'valueType' in value &&
		'setters' in value
	);
}
